import React, { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaShieldAlt, FaStar, FaCreditCard, FaMobileAlt, FaRobot, FaBolt, FaLock, FaChartLine, FaUserTie, FaFileInvoice, FaHistory, FaComments, FaBell } from 'react-icons/fa';
import { MdVerified, MdAir, MdHandyman, MdSpeed } from 'react-icons/md';
import { IoBody } from 'react-icons/io5';
import { GiMagicBroom } from 'react-icons/gi';
import { FaCcVisa, FaCcMastercard, FaCcAmex, FaCcApplePay, FaGooglePay } from 'react-icons/fa';
import { BsArrowRight, BsLightningChargeFill, BsStars } from 'react-icons/bs';
import { HiOutlineSparkles } from 'react-icons/hi';
import { s3Static } from '../../utils/constants';
import { ShieldCheck } from 'lucide-react';
import { googleRating, googleReviewCount, gbpUrl } from '../../utils/business-info';

// Safari flickering fix styles - prevents GPU compositing issues
const safariFlickerFix = {
  backfaceVisibility: 'hidden',
  WebkitBackfaceVisibility: 'hidden',
  transform: 'translate3d(0, 0, 0)',
  WebkitTransform: 'translate3d(0, 0, 0)',
  perspective: 1000,
  WebkitPerspective: 1000
};

// Service data with 3D images
// Services data
const services = [
  {
    icon: IoBody,
    title: 'Personal Trainer Singapore',
    description: 'In-house certified trainers employed directly by StringsSG. Customized programs for home, condo gym, or outdoor sessions.',
    ctaText: 'Browse personal trainers near you',
    image: s3Static + 'personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg',
    link: '/sg/personal-trainer',
    highlight: 'In-House Employed',
    color: 'from-rose-500 to-pink-600'
  },
  {
    icon: MdAir,
    title: 'Aircon Servicing Singapore',
    description: 'AI-powered SMART Pricing calculates real-time costs. Vetted technicians, StringsSG service standards and same-day service available.',
    ctaText: 'View aircon servicing prices',
    image: s3Static + 'blog/living/aircon/chinese-aircon-service-man-smiling.jpg',
    link: '/sg/aircon-servicing-singapore',
    highlight: 'AI SMART Pricing',
    color: 'from-sky-500 to-blue-600'
  },
  {
    icon: MdHandyman,
    title: 'Handyman Services Singapore',
    description: '24-hour emergency support for plumbing, electrical, carpentry, and home repairs. Background-checked professionals.',
    ctaText: 'See handyman services in Singapore',
    image: s3Static + 'handyman/handyman-singapore.jpg',
    link: '/sg/handyman-singapore-services',
    highlight: '24/7 Emergency',
    color: 'from-amber-500 to-orange-600'
  },
  {
    icon: GiMagicBroom,
    title: 'Part-Time Helper Singapore',
    description: 'Vetted helpers for housekeeping, deep cleaning, laundry, and household chores. Flexible scheduling.',
    ctaText: 'Book a part-time helper in Singapore',
    image: s3Static + 'part-time-helper/part-time-helper-in-gray-uniform-living.jpg',
    link: '/sg/part-time-cleaner-singapore',
    highlight: 'Background Verified',
    color: 'from-emerald-500 to-teal-600'
  }
]

// Platform features
const platformFeatures = [
  {
    icon: FaRobot,
    title: 'RedDotAI Technology',
    description: 'Our proprietary AI model launched in October 2025, designed to enhance localized customer service and smart operations.'
  },
  {
    icon: FaChartLine,
    title: 'SMART Dynamic Pricing',
    description: 'AI-powered and data-driven algorithms calculate real-time costs based on your specific requirements. Transparent and competitive rates.'
  },
  {
    icon: FaMobileAlt,
    title: 'Seamless Experience',
    description: 'Book, pay, and track services through our intuitive web and mobile apps. Digital invoices and service records in one place.'
  },
  {
    icon: FaLock,
    title: 'Secure Payments',
    description: 'TLS 1.3 encryption with support for PayNow, cards, Apple Pay, and Google Pay.'
  },
  {
    icon: FaShieldAlt,
    title: 'Vetted Professionals',
    description: 'Rigorous ACRA verification and background checks. Our scoring system ensures quality and accountability.'
  },
  {
    icon: FaBell,
    title: 'Smart Notifications',
    description: 'Automated service reminders, real-time updates, and instant communication through our in-built chat system.'
  }
];

// Stats
const stats = [
  { value: '2018', label: 'Founded' },
  { value: '100K+', label: 'Services Completed' },
  { value: '20K+', label: 'Happy Clients' },
  { value: googleRating, label: 'Avg Rating', hasStar: true }
];

// Hero Section with Video
const HeroSection = () => {
  const videoRef = useRef();
  // The 4.5MB hero video was the mobile LCP element. Mount it only after a
  // client-side width check so phones never request it at all - CSS alone
  // (hidden md:block) would still download it.
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    if (!globalThis.matchMedia) return;
    const mq = globalThis.matchMedia('(min-width: 768px)');
    const sync = () => setShowVideo(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (showVideo && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [showVideo]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Hero still - the LCP element on every viewport */}
      <Image
        src={s3Static + "girl-with-stringssg-logo-on-mobile.jpg"}
        alt="StringsSG aircon technicians and personal trainers serving homes across Singapore"
        fill
        priority
        sizes="100vw"
        className="absolute inset-0 object-cover"
      />

      {/* Video Background - desktop only */}
      {showVideo && (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          poster={s3Static + "girl-with-stringssg-logo-on-mobile.jpg"}
          muted
          loop
          playsInline
          preload="none"
        >
          <source src="/video/main-least.mp4" type="video/mp4" />
          <source src="/video/main-least.mov" type="video/quicktime" />
        </video>
      )}
      
      {/* Vibrant Multi-color Gradient Overlay - orange, amber, red, pink, indigo */}
      <div className="absolute inset-0 bg-linear-to-br from-orange-600/70 via-red-500/60 to-pink-600/50" />
      <div className="absolute inset-0 bg-linear-to-tr from-indigo-600/40 via-amber-500/30 to-transparent" />
      
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute -top-60 left-0 w-100 h-100 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
      </div>
      
      {/* Animated Waves - Covering 60% from bottom - Indigo/Blue variations */}
      <div className="absolute bottom-0 left-0 right-0 h-[60%] z-20 pointer-events-none overflow-hidden">
        {/* Wave Layer 1 - Slowest, Back - Deep Indigo */}
        <motion.div
          className="absolute bottom-0 left-0 w-[200%] h-full"
          animate={{ 
            x: ["0%", "-50%"]
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity, 
            ease: "linear"
          }}
        >
          <svg className="w-full h-full" viewBox="0 0 2400 400" preserveAspectRatio="none">
            <path 
              d="M0,200 C200,280 400,120 600,200 C800,280 1000,120 1200,200 C1400,280 1600,120 1800,200 C2000,280 2200,120 2400,200 L2400,400 L0,400 Z"
              fill="rgba(79,70,229,0.15)"
            />
          </svg>
        </motion.div>
        
        {/* Wave Layer 2 - Medium speed - Blue */}
        <motion.div
          className="absolute bottom-0 left-0 w-[200%] h-[80%]"
          animate={{ 
            x: ["-25%", "0%", "-25%"]
          }}
          transition={{ 
            duration: 12, 
            repeat: Infinity, 
            ease: "easeInOut"
          }}
        >
          <svg className="w-full h-full" viewBox="0 0 2400 320" preserveAspectRatio="none">
            <path 
              d="M0,160 C300,240 600,80 900,160 C1200,240 1500,80 1800,160 C2100,240 2400,80 2400,160 L2400,320 L0,320 Z"
              fill="rgba(59,130,246,0.18)"
            />
          </svg>
        </motion.div>
        
        {/* Wave Layer 3 - Fastest, Front - Sky Blue */}
        <motion.div
          className="absolute bottom-0 left-0 w-[200%] h-[60%]"
          animate={{ 
            x: ["0%", "-50%"]
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity, 
            ease: "linear"
          }}
        >
          <svg className="w-full h-full" viewBox="0 0 2400 240" preserveAspectRatio="none">
            <path 
              d="M0,120 C150,180 300,60 450,120 C600,180 750,60 900,120 C1050,180 1200,60 1350,120 C1500,180 1650,60 1800,120 C1950,180 2100,60 2250,120 C2400,180 2400,60 2400,120 L2400,240 L0,240 Z"
              fill="rgba(14,165,233,0.2)"
            />
          </svg>
        </motion.div>
        
        {/* Wave Layer 4 - Accent wave - Light Indigo */}
        <motion.div
          className="absolute bottom-0 left-0 w-[200%] h-[40%]"
          animate={{ 
            x: ["-10%", "-60%", "-10%"]
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity, 
            ease: "easeInOut"
          }}
        >
          <svg className="w-full h-full" viewBox="0 0 2400 160" preserveAspectRatio="none">
            <path 
              d="M0,80 C200,120 400,40 600,80 C800,120 1000,40 1200,80 C1400,120 1600,40 1800,80 C2000,120 2200,40 2400,80 L2400,160 L0,160 Z"
              fill="rgba(99,102,241,0.22)"
            />
          </svg>
        </motion.div>
      </div>
      
      <div className="absolute inset-0 bg-black/15" />
      
      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 py-20 text-center">
        <div className="space-y-6">
          {/* Badge */}
          {/* <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2.5 text-white text-sm font-medium">
            <BsStars className="text-amber-300 text-lg" />
            <span>Singapore&apos;s #1 Home Service Platform</span>
          </div> */}

          {/* Main Headline */}
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-black text-white leading-tight tracking-tight">
            Aircon Servicing &amp; Personal Trainers{' '}
            <span className="block bg-linear-to-r from-amber-200 via-yellow-100 to-white bg-clip-text text-transparent">
              in Singapore
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed font-light">
            Plus handyman and part-time helper services. StringsSG service standards on every job, in-house
            certified trainers and transparent AI-powered pricing.
            <span className="font-semibold"> Not a marketplace — </span>
            we&apos;re your complete service solution since 2018.
          </p>
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
            <Link
              href="/sg/aircon-servicing-singapore"
              className="inline-flex items-center justify-center gap-2 bg-white text-orange-600 font-bold px-8 py-4 rounded-full text-lg transition-all duration-300 hover:bg-orange-50 hover:scale-105 shadow-2xl"
            >
              Book Aircon Servicing
              <BsArrowRight className="text-xl" />
            </Link>
            <Link
              href="/sg/personal-trainer"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border-2 border-white text-white font-bold px-8 py-4 rounded-full text-lg transition-all duration-300 hover:bg-white/20 hover:scale-105 shadow-2xl"
            >
              Find a Personal Trainer
              <BsArrowRight className="text-xl" />
            </Link>
          </div>          
          {/* Company Trust Badge */}
          <div className="">
              <div className="flex items-center justify-center gap-1 text-gray-700">
                  <ShieldCheck className="w-5 h-5 text-green-500" />
                  <p className="text-sm text-white">
                      StringsSG Pte Ltd &middot; UEN 201813375G
                  </p>
              </div>
          </div>
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-6 pt-5 text-white text-sm">
            <span className="flex items-center gap-2">
              <FaCheckCircle className="text-amber-300" />
              StringsSG Service Standards
            </span>
            <span className="flex items-center gap-2">
              <FaCheckCircle className="text-amber-300" />
              In-House Trainers
            </span>
            <span className="flex items-center gap-2">
              <FaCheckCircle className="text-amber-300" />
              AI-Powered
            </span>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/80 rounded-full" />
        </div>
      </div>
    </section>
  );
};

// Stats Banner
const StatsBanner = () => (
  <section className="bg-linear-to-r from-slate-900 via-slate-800 to-slate-900 py-12">
    <div className="max-w-6xl mx-auto px-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        {stats.map((stat, index) => (
          <div key={index} className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              {stat.hasStar && <FaStar className="text-amber-400 text-2xl md:text-3xl" />}
              <span className="text-3xl md:text-4xl lg:text-5xl font-black text-white">{stat.value}</span>
            </div>
            <p className="text-orange-400 font-semibold text-sm uppercase tracking-wider">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Technology Section
const TechnologySection = () => (
  <section id="technology" className="py-20 md:py-32 bg-white">
    <div className="max-w-6xl mx-auto px-4">
      {/* Header */}
      <div className="text-center mb-16">
        <div>
          <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <BsLightningChargeFill />
            TECHNOLOGY
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 mb-4">
            Built on Innovation
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Advanced AI and machine learning powering seamless service experiences across Singapore.
          </p>
        </div>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {platformFeatures.map((feature, index) => (
          <div
            key={index}
            className="group p-8 bg-linear-to-br from-slate-50 to-white border border-slate-100 rounded-2xl"
          >
            <div 
              className="w-14 h-14 bg-linear-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center mb-5"
              style={safariFlickerFix}
            >
              <feature.icon className="text-2xl text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
            <p className="text-slate-600 leading-relaxed">{feature.description}</p>
          </div>
        ))}
      </div>

      {/* App Showcase */}
      <div className="mt-20 relative">
        <div className="bg-linear-to-br from-orange-500 via-orange-600 to-amber-600 rounded-3xl p-8 md:p-12 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="text-white space-y-6">
              <h3 className="text-2xl md:text-3xl font-bold">One-Stop Home Service Mobile App</h3>
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <FaMobileAlt className="text-amber-200" />
                  </div>
                  <span>Book, track, manage & schedule/re-schedule services</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <FaCreditCard className="text-amber-200" />
                  </div>
                  <span>Secure payment integration</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <FaComments className="text-amber-200" />
                  </div>
                  <span>In-built chat & support</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <FaFileInvoice className="text-amber-200" />
                  </div>
                  <span>Anytime access: digital receipts, service records & contracts</span>
                </li>
              </ul>
              
              {/* App Store Links */}
              <div className="pt-4">
                <p className="text-white/80 text-sm mb-3">Download the app:</p>
                <div className="flex flex-wrap gap-3">
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.stringssg.app" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="transition-transform hover:scale-105"
                  >
                    <Image 
                      src={s3Static + "google-play-badge.png"} 
                      alt="Get it on Google Play" 
                      width={140} 
                      height={42}
                      className="h-10.5 w-auto"
                    />
                  </a>
                  <a 
                    href="https://itunes.apple.com/us/app/stringssg/id1347513244" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="transition-transform hover:scale-105 bg-white rounded-lg"
                  >
                    <Image 
                      src={s3Static + "apple-store-badge.png"} 
                      alt="Download on the App Store" 
                      width={140} 
                      height={42}
                      className="h-10.5 w-auto"
                    />
                  </a>
                </div>
              </div>
            </div>
            <div className="flex justify-center items-end gap-4">
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <Image 
                  height={400} 
                  width={180} 
                  src={s3Static + "stringssg-mobile-app.png"} 
                  alt="StringsSG Mobile App"
                  className="rounded-2xl shadow-2xl"
                />
              </motion.div>
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
                className="hidden md:block"
              >
                <Image 
                  height={350} 
                  width={450} 
                  src={s3Static + "stringssg-tablet-view.png"} 
                  alt="StringsSG Tablet View"
                  className="rounded-xl shadow-2xl"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services Section
const ServicesSection = () => (
  <section id="services" className="py-20 md:py-32 bg-slate-50">
    <div className="max-w-6xl mx-auto px-4">
      {/* Header */}
      <div className="text-center mb-16">
        <div>
          <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <MdVerified />
            OUR SERVICES
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 mb-4">
            Aircon Servicing, Personal Training, Handyman &amp; Helper Services in Singapore
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            From in-house employed personal trainers to vetted technicians and helpers, everyone who delivers for StringsSG works to our rigorous standards.
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
            >
              <Link href={service.link} className="group block">
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="relative h-60 overflow-hidden">
                    <Image
                      src={service.image}
                      alt={service.title}
                      fill
                      sizes="(max-width: 768px) 100vw, 33vw"
                      className="object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className={`inline-flex items-center gap-1.5 bg-linear-to-r ${service.color} text-white text-xs font-bold px-3 py-1.5 rounded-full`}>
                        <MdVerified />
                        {service.highlight}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 bg-linear-to-br ${service.color} rounded-xl flex items-center justify-center`}>
                        <service.icon className="text-xl text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900">{service.title}</h3>
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed">{service.description}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-orange-600 font-semibold text-sm group-hover:gap-2.5 transition-all">
                      {service.ctaText}
                      <BsArrowRight />
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
    </div>
  </section>
);

// Trust Section for Customers
const TrustSectionCustomers = () => (
  <section className="py-20 md:py-32 bg-white">
    <div className="max-w-6xl mx-auto px-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Content */}
        <div>
          <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <FaShieldAlt />
            FOR CUSTOMERS
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-6">
            Why Customers Choose StringsSG
          </h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            Experience peace of mind with verified professionals, transparent pricing, and complete accountability. We&apos;re not just a marketplace — we&apos;re your trusted service partner.
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                <FaCheckCircle className="text-green-600" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900">Vetted Professionals</h4>
                <p className="text-slate-600">Everyone who delivers for StringsSG is vetted and background checked, and follows the service steps we define</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                <FaCheckCircle className="text-green-600" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900">AI-Powered SMART Pricing</h4>
                <p className="text-slate-600">Transparent, competitive rates calculated in real-time based on your needs</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                <FaCheckCircle className="text-green-600" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900">Consistent Service Quality</h4>
                <p className="text-slate-600">Performance scoring system ensures high standards across all service professionals</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                <FaCheckCircle className="text-green-600" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900">Dedicated Customer Support</h4>
                <p className="text-slate-600">RedDotAI-powered assistance and responsive support team ready to help</p>
              </div>
            </div>
          </div>
        </div>

        {/* Visual */}
        <div className="relative">
          <div className="bg-linear-to-br from-blue-500 to-indigo-600 rounded-3xl p-8 md:p-10 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
            </div>
            <div className="relative text-white text-center">
              <FaStar className="text-6xl mx-auto mb-6 text-yellow-300" />
              <h3 className="text-2xl font-bold mb-4">Quality Guaranteed</h3>
              <p className="text-white/90 mb-6">
                Our performance scoring system ensures you always get the best service professionals.
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-3xl font-black mb-1">{googleRating}</div>
                  <span>Avg Rating</span>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <div className="text-3xl font-black mb-1">100K+</div>
                  <span>Jobs Done</span>
                </div>
              </div>
              {/* Shown as ordinary page content, not JSON-LD. Google's
                  review-snippet rules make a business ineligible for stars on
                  reviews it controls about itself, and forbid aggregating
                  ratings from other sites - so this is never marked up. */}
              <a
                href={gbpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-block text-white/80 text-sm underline hover:text-white"
              >
                Based on {googleReviewCount} Google reviews
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Trust Section for Service Partners
const TrustSectionPartners = () => (
  <section className="py-20 md:py-32 bg-slate-50">
    <div className="max-w-6xl mx-auto px-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Visual */}
        <div className="relative order-2 lg:order-1">
          <div className="bg-linear-to-br from-orange-500 to-amber-500 rounded-3xl p-8 md:p-10 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
            </div>
            <div className="relative text-white text-center">
              <FaUserTie className="text-6xl mx-auto mb-6 opacity-90" />
              <h3 className="text-2xl font-bold mb-4">e-Business Card</h3>
              <p className="text-white/90 mb-6">
                Your professional digital profile to showcase your skills, ratings, and grow your reputation.
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-white/10 rounded-xl p-4">
                  <MdSpeed className="text-3xl mx-auto mb-2" />
                  <span>Fast Onboarding</span>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <FaChartLine className="text-3xl mx-auto mb-2" />
                  <span>Growth Tools</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="order-1 lg:order-2">
          <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <FaUserTie />
            FOR SERVICE PARTNERS
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-6">
            Grow Your Business with StringsSG
          </h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            Focus on what you do best — delivering exceptional service. We handle the marketing, scheduling, invoicing, and customer acquisition for you.
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                <FaCheckCircle className="text-orange-600" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900">Zero Marketing Costs</h4>
                <p className="text-slate-600">We bring customers to you — no advertising spend required</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                <FaCheckCircle className="text-orange-600" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900">Automated Operations</h4>
                <p className="text-slate-600">Scheduling, invoicing, and job management handled by our platform</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                <FaCheckCircle className="text-orange-600" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900">Secure Payments</h4>
                <p className="text-slate-600">Get paid reliably through our secure payment system</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                <FaCheckCircle className="text-orange-600" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900">Build Your Reputation</h4>
                <p className="text-slate-600">Earn ratings, reviews, and grow your professional profile</p>
              </div>
            </div>
          </div>
          <div className="mt-8">
            <Link
              href="/service-provider-signup"
              className="inline-flex items-center gap-2 bg-linear-to-r from-orange-500 to-amber-500 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:gap-3"
            >
              Become a Partner
              <BsArrowRight />
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Payment Section
const PaymentSection = () => (
  <section className="py-16 md:py-24 bg-slate-900">
    <div className="max-w-4xl mx-auto px-4 text-center">
      <div>
        <FaLock className="text-4xl text-orange-400 mx-auto mb-6" />
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
          Secure End-to-End Payments
        </h2>
        <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
          Protected by TLS 1.3 encryption. Pay securely with your preferred method.
        </p>
        <div className="flex flex-wrap justify-center gap-4 text-5xl md:text-6xl text-slate-500 mb-8">
          <FaCcVisa className="hover:text-blue-500 transition-colors cursor-pointer" />
          <FaCcMastercard className="hover:text-red-500 transition-colors cursor-pointer" />
          <FaCcAmex className="hover:text-blue-400 transition-colors cursor-pointer" />
          <FaCcApplePay className="hover:text-white transition-colors cursor-pointer" />
          <FaGooglePay className="hover:text-white transition-colors cursor-pointer" />
        </div>
        <p className="text-orange-400 font-semibold">+ PayNow supported</p>
      </div>
    </div>
  </section>
);

// Topical content for the priority search terms. Doubles as the homepage's
// contextual link block into the aircon, personal-trainer, handyman and
// electrician hub clusters - these are the only homepage links the deeper SEO
// landing pages receive. The handyman and electrician copy quotes no prices:
// those come from the price API, which this page (no getStaticProps) cannot read.
const localServices = [
  {
    heading: 'Aircon Servicing in Singapore',
    hub: { href: '/sg/aircon-servicing-singapore', label: 'aircon servicing in Singapore' },
    body: (
      <>
        StringsSG handles aircon servicing across Singapore from{' '}
        <strong>$16 per unit</strong> (five units, before transport), and every quote already
        includes transport. Our
        Our vetted technicians cover general servicing, chemical wash and chemical
        overhaul, gas top-up, and repairs for units that are not cooling, leaking water
        or making noise. We service all major brands — Daikin, Mitsubishi Electric,
        Panasonic, LG and Samsung — for HDB flats, condos and landed homes island-wide,
        with same-day slots available.
      </>
    ),
    links: [
      { href: '/sg/aircon-servicing-singapore/aircon-servicing-price-singapore', label: 'Aircon servicing price in Singapore' },
      { href: '/sg/aircon-servicing-singapore/aircon-chemical-wash-and-overhaul', label: 'Aircon chemical wash & overhaul' },
      { href: '/sg/aircon-servicing-singapore/aircon-repair', label: 'Aircon repair' },
      { href: '/sg/aircon-servicing-singapore/aircon-gas-top-up', label: 'Aircon gas top-up' },
      { href: '/sg/aircon-servicing-singapore/aircon-servicing-near-me', label: 'Aircon servicing near me' },
    ],
  },
  {
    heading: 'Personal Trainer Singapore',
    hub: { href: '/sg/personal-trainer', label: 'certified personal trainers in Singapore' },
    body: (
      <>
        Book a certified personal trainer in Singapore from{' '}
        <strong>$84 per session</strong> — no gym membership required. Our trainers are
        employed in-house rather than listed on a marketplace, so every programme is
        vetted and accountable. Train at home, in your condo gym or at a nearby park,
        with specialists for weight loss, muscle gain, functional fitness, prenatal and
        postpartum training, and seniors. Every client starts with a free consultation.
      </>
    ),
    links: [
      { href: '/sg/personal-trainer/personal-trainer-rates-singapore', label: 'Personal trainer rates in Singapore' },
      { href: '/sg/personal-trainer/personal-trainer-singapore-female', label: 'Female personal trainers' },
      { href: '/sg/personal-trainer/freelance-personal-trainer-singapore', label: 'Freelance personal trainers' },
      { href: '/sg/personal-trainer/private-home-trainer', label: 'Private home trainer' },
      { href: '/sg/personal-trainer/packages', label: 'Personal training packages' },
    ],
  },
  {
    heading: 'Handyman Services in Singapore',
    hub: { href: '/sg/handyman-singapore-services', label: 'handyman services in Singapore' },
    body: (
      <>
        Book a handyman in Singapore at <strong>fixed, published prices</strong>, with no
        hourly billing. Our handymen fix leaks and choked toilets, replace door locks and
        handles, mount TVs and mirrors, install curtain rails, assemble furniture and paint
        HDB flats, with same-day slots from 8am to 10pm daily.
      </>
    ),
    links: [
      { href: '/sg/handyman-singapore-services/handyman-price-list', label: 'Handyman price list Singapore' },
      { href: '/sg/handyman-singapore-services/tv-mounting', label: 'TV wall mounting' },
      { href: '/sg/handyman-singapore-services/curtain-rail-installation', label: 'Curtain rail installation' },
      { href: '/sg/handyman-singapore-services/door-lock-replacement', label: 'Door lock replacement' },
      { href: '/sg/handyman-singapore-services/furniture-assembly', label: 'Furniture assembly' },
    ],
  },
  {
    heading: 'Electrician Services in Singapore',
    hub: { href: '/sg/electrician-services-singapore', label: 'licensed electricians in Singapore' },
    body: (
      <>
        Licensed electricians for HDB flats, condos and offices across Singapore, at a{' '}
        <strong>fixed price per job</strong> you see before you book. We handle power trips,
        wiring repairs, circuit breakers and DB boxes, lighting, ceiling fans, power points
        and water heaters, with same-day slots available.
      </>
    ),
    links: [
      { href: '/sg/electrician-services-singapore/electrician-price-singapore', label: 'Electrician price list Singapore' },
      { href: '/sg/electrician-services-singapore/power-trip-repair', label: 'Power trip repair' },
      { href: '/sg/electrician-services-singapore/db-box-replacement', label: 'DB box replacement' },
      { href: '/sg/electrician-services-singapore/ceiling-fan-installation', label: 'Ceiling fan installation' },
      { href: '/sg/electrician-services-singapore/water-heater-installation', label: 'Water heater installation' },
    ],
  },
];

const LocalServicesSection = () => (
  <section className="py-20 md:py-28 bg-white">
    <div className="max-w-6xl mx-auto px-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
        {localServices.map((item, index) => (
          <div key={index}>
            <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-4">
              {item.heading}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">{item.body}</p>
            <ul className="space-y-2.5 mb-6">
              {item.links.map((link, i) => (
                <li key={i}>
                  <Link
                    href={link.href}
                    prefetch={false}
                    className="text-slate-700 hover:text-orange-600 text-sm inline-flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-orange-500 transition-colors" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              href={item.hub.href}
              className="inline-flex items-center gap-2 text-orange-600 font-bold hover:gap-3 transition-all"
            >
              See all {item.hub.label}
              <BsArrowRight />
            </Link>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="py-20 md:py-32 bg-linear-to-br from-orange-500 via-orange-600 to-amber-600">
    <div className="max-w-4xl mx-auto px-4 text-center">
      <div className="space-y-8">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white">
          Ready to Experience the Difference?
        </h2>
        <p className="text-xl text-white/90 max-w-2xl mx-auto">
          Join thousands of satisfied customers who trust StringsSG for their home service needs.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            href="/signup"
            className="inline-flex items-center justify-center gap-2 bg-white text-orange-600 font-bold px-10 py-4 rounded-full text-lg transition-all duration-300 hover:scale-105 shadow-2xl"
          >
            Sign Up
            <BsArrowRight className="text-xl" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

// Main Component
export const ModernHomePage = () => {
  return (
    <div className="min-h-screen -mt-14">
      <HeroSection />
      <StatsBanner />
      <ServicesSection />
      <LocalServicesSection />
      <TrustSectionCustomers />
      <CTASection />
      <TechnologySection />
      <TrustSectionPartners />
      <PaymentSection />
    </div>
  );
};

export default ModernHomePage;
